import React from 'react';
import { Check, ArrowRight, BookOpen, Users, Shield, Video, Monitor, Presentation, Package } from 'lucide-react';
import { PageTransition } from '../components/layout/PageTransition';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DecorativeShape } from '../components/DecorativeShape';
import { Animate } from '../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
export function Courses() {
  const {
    t
  } = useLanguage();
  const courses = [{
    id: 'conflict-coaching',
    path: '/services/courses/conflict-coaching',
    icon: <Shield size={32} data-id="element-270" />,
    color: 'bg-coral',
    lightColor: 'bg-coral/10',
    textColor: 'text-coral',
    borderColor: 'border-coral/20',
    tKey: 'conflictCoaching'
  }, {
    id: 'first-time-manager',
    path: '/services/courses/first-time-manager',
    icon: <Users size={32} data-id="element-271" />,
    color: 'bg-teal',
    lightColor: 'bg-teal/10',
    textColor: 'text-teal',
    borderColor: 'border-teal/20',
    tKey: 'firstTimeManager'
  }, {
    id: 'better-online-meetings',
    path: '/services/courses/better-online-meetings',
    icon: <Video size={32} data-id="element-272" />,
    color: 'bg-blue-600',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    tKey: 'betterOnlineMeetings'
  }, {
    id: 'better-online-workshops',
    path: '/services/courses/better-online-workshops',
    icon: <Presentation size={32} data-id="element-273" />,
    color: 'bg-purple-600',
    lightColor: 'bg-purple-50',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-200',
    tKey: 'betterOnlineWorkshops'
  }, {
    id: 'modern-product-management',
    path: '/services/courses/modern-product-management',
    icon: <Package size={32} data-id="element-274" />,
    color: 'bg-amber-600',
    lightColor: 'bg-amber-50',
    textColor: 'text-amber-600',
    borderColor: 'border-amber-200',
    tKey: 'modernProductManagement'
  }];
  return <PageTransition data-id="element-275">
      <div className="pt-12 pb-24" data-id="element-276">
        <section className="text-center max-w-4xl mx-auto px-4 mb-20 relative" data-id="element-277">
          <DecorativeShape type="square" color="bg-coral/10" className="top-0 left-0 w-32 h-32 -rotate-12 -z-10" data-id="element-278" />
          <DecorativeShape type="donut" color="border-teal/10" className="bottom-0 right-0 w-40 h-40 -z-10" data-id="element-279" />
          <Animate data-id="element-280">
            <span className="inline-block px-4 py-2 bg-coral/10 text-coral font-medium rounded-full text-sm mb-6" data-id="element-281">
              {t('courses.badge')}
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-id="element-282">
              {t('courses.title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-283">
              {t('courses.subtitle')}
            </p>
          </Animate>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-284">
          <div className="space-y-12" data-id="element-285">
            {courses.map((course, index) => {
            const benefits = t(`courses.items.${course.tKey}.benefits`) as string[];
            return <Animate key={course.id} delay={0} data-id="element-286">
                  <Card className={`p-0 overflow-hidden border-2 ${course.borderColor}`} data-id="element-287">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-0" data-id="element-288">
                      <div className={`lg:col-span-4 ${course.lightColor} p-10 flex flex-col justify-center items-center text-center relative overflow-hidden`} data-id="element-289">
                        <div className={`w-24 h-24 rounded-full ${course.color} text-white flex items-center justify-center mb-6 shadow-lg relative z-10`} data-id="element-290">
                          {course.icon}
                        </div>
                        <h3 className={`text-2xl font-bold ${course.textColor} mb-2 relative z-10`} data-id="element-291">
                          {t(`courses.items.${course.tKey}.title`)}
                        </h3>
                        <p className="font-medium text-gray-600 relative z-10 mb-4" data-id="element-292">
                          {t(`courses.items.${course.tKey}.subtitle`)}
                        </p>
                      </div>
                      <div className="lg:col-span-8 p-8 md:p-12 flex flex-col justify-center bg-white" data-id="element-293">
                        <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-294">
                          {t(`courses.items.${course.tKey}.description`)}
                        </p>
                        <div className="flex flex-wrap gap-6 mb-8 text-sm" data-id="element-295">
                          <div className="flex items-center gap-2 text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg" data-id="element-296">
                            <Monitor size={18} className={course.textColor} data-id="element-297" />
                            <span className="font-medium" data-id="element-298">
                              {t(`courses.items.${course.tKey}.format`)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg" data-id="element-299">
                            <Users size={18} className={course.textColor} data-id="element-300" />
                            <span className="font-medium" data-id="element-301">
                              {t(`courses.items.${course.tKey}.audience`)}
                            </span>
                          </div>
                        </div>
                        <div className="mb-8" data-id="element-302">
                          <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4" data-id="element-303">
                            {t('courses.benefitsTitle')}
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-304">
                            {benefits.map((benefit: string, i: number) => <div key={i} className="flex items-start" data-id="element-305">
                                <div className={`mt-1 mr-3 p-1 rounded-full ${course.lightColor} ${course.textColor}`} data-id="element-306">
                                  <Check size={12} strokeWidth={3} data-id="element-307" />
                                </div>
                                <span className="text-gray-700" data-id="element-308">{benefit}</span>
                              </div>)}
                          </div>
                        </div>
                        <div className="pt-6 border-t border-gray-100" data-id="element-309">
                          <Link to={course.path} data-id="element-310">
                            <Button variant={index === 1 ? 'secondary' : 'primary'} className={index === 4 ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-200' : index === 3 ? 'bg-purple-600 hover:bg-purple-700 shadow-purple-200' : index === 2 ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-200' : ''} rightIcon={<ArrowRight size={18} data-id="element-312" />} data-id="element-311">
                              {t('courses.moreInfo')}
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Animate>;
          })}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-313">
          <Animate data-id="element-314">
            <Card className="p-12 bg-gradient-to-br from-teal/5 to-coral/5 border-0" data-id="element-315">
              <div className="w-16 h-16 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-sm" data-id="element-316">
                <BookOpen size={32} className="text-charcoal" data-id="element-317" />
              </div>
              <h2 className="text-3xl font-bold mb-4" data-id="element-318">
                {t('courses.customTitle')}
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-319">
                {t('courses.customDescription')}
              </p>
              <Link to="/contact" data-id="element-320">
                <Button variant="secondary" rightIcon={<ArrowRight size={18} data-id="element-322" />} data-id="element-321">
                  {t('courses.contactUs')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}